import dayjs from 'dayjs'
import { Action } from 'redux'
import { combineEpics, Epic, ofType } from 'redux-observable'
import {
  catchError,
  concatMap,
  EMPTY,
  exhaustMap,
  ignoreElements,
  map,
  merge,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
  timer
} from 'rxjs'
import config from '../../config'
import { formatError } from '../../helpers/formatError'
import { getNow } from '../../helpers/auth'
import { getHub } from '../../helpers/hub'
import { redirectToLogin } from '../auth/actions'
import { getAllSecurities } from '../securities/actions'
import { State as AuthState } from '../auth/reducer'
import { getAuthToken } from '../auth/selectors'
import { beginHeartbeating } from '../heartbeat/actions'
import { fetchUserPreferences } from '../userPreferences/actions'
import { fetchUserPermissions } from '../webSettings/actions'
import {
  adjustServerTimeDelaySuccess,
  connectFailure,
  connectSuccess,
  initTime,
  logError,
  LogErrorAction,
  LogServerMetadataAction,
  WsConnectAction
} from './actions'

import {
  cancelForceReloadTimer,
  startForceReloadTimer
} from './forceReloadTimer'

const connectEpic: Epic<Action, Action, { auth: AuthState }> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('ws.connect'),
    switchMap((action: WsConnectAction) => {
      const state = state$.value
      const authToken = getAuthToken(state)
      return getHub()
        .start(config.api.url, authToken ?? '')
        .pipe(
          concatMap(() =>
            of(
              connectSuccess(),
              fetchUserPreferences(),
              beginHeartbeating(),
              fetchUserPermissions(),
              getAllSecurities(),
              initTime(dayjs().toDate())
            )
          ),
          catchError((err) => {
            if (err?.message === 'Unauthorized') {
              return of(
                connectFailure(err),
                logError('Invalid token, redirecting to login…'),
                redirectToLogin()
              )
            }
            console.log(err)
            return merge(
              of(connectFailure(err), logError(err)),
              timer(config.api.reconnectDelay).pipe(map(() => action))
            )
          })
        )
    })
  )

const adjustServerTimeDelayEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('ws.adjust-server-time'),
    mergeMap(() => {
      const startTime = getNow()
      return getHub()
        .invoke<string>('GetTime')
        .pipe(
          map((time) => {
            const endTime = getNow()
            const serverTime = new Date(time).valueOf()
            const delay = serverTime - (endTime + startTime) / 2
            return adjustServerTimeDelaySuccess(delay)
          }),
          catchError((err) => of(logError(err)))
        )
    })
  )

const logErrorsEpic: Epic = (action$) =>
  action$.pipe(
    ofType<LogErrorAction>('ws.log-error'),
    // tslint:disable-next-line: no-console
    tap((action) =>
      console.error(
        action.payload instanceof Error
          ? action.payload
          : formatError(action.payload)
      )
    ),
    mergeMap(() => EMPTY)
  )

const logServerMetadataEpic: Epic = (action$) =>
  action$.pipe(
    ofType<LogServerMetadataAction>('ws.log-server-metadata'),
    mergeMap(() =>
      getHub()
        .invoke('GetServerMetadata')
        .pipe(
          // eslint-disable-next-line no-console
          tap((metadata) => console.log('Server metadata:', metadata)),
          ignoreElements(),
          catchError((err) => of(logError(err)))
        )
    )
  )

const forceReloadEpic: Epic = (action$) =>
  action$.pipe(
    ofType('ws.force-reload'),
    exhaustMap(() => {
      startForceReloadTimer()
      return action$.pipe(
        ofType('ws.cancel-force-reload'),
        take(1),
        tap(() => cancelForceReloadTimer())
      )
    }),
    ignoreElements()
  )

export default combineEpics(
  connectEpic,
  adjustServerTimeDelayEpic,
  logErrorsEpic,
  logServerMetadataEpic,
  forceReloadEpic
)
